/**
 * Ghost Protocol — Token Management
 *
 * Keeps the admin token safe in a separate key so that
 * "Open in New Tab" impersonation doesn't overwrite the admin session.
 */

const KEYS = {
  TOKEN: 'buzzbit_token',
  ADMIN_TOKEN: 'buzzbit_admin_token_backup',
  WORKSPACE: 'buzzbit_workspace',
  ADMIN_WORKSPACE: 'buzzbit_workspace_backup',
  IS_IMPERSONATING: 'buzzbit_impersonating',
  IMPERSONATION_REASON: 'buzzbit_impersonation_reason',
  IMPERSONATION_TARGET: 'buzzbit_impersonation_target',
} as const;

/**
 * Start impersonation — saves current admin token before overwriting
 */
export function startImpersonation(
  impersonationToken: string,
  targetWorkspaceId: string,
  targetUserId: string,
  reason: string
): void {
  // 1. Save current token BEFORE overwriting
  const currentToken = localStorage.getItem(KEYS.TOKEN);
  const currentWorkspace = localStorage.getItem(KEYS.WORKSPACE);

  if (currentToken) {
    localStorage.setItem(KEYS.ADMIN_TOKEN, currentToken);
  }
  if (currentWorkspace) {
    localStorage.setItem(KEYS.ADMIN_WORKSPACE, currentWorkspace);
  }

  // 2. Set impersonation token as active
  localStorage.setItem(KEYS.TOKEN, impersonationToken);
  localStorage.setItem(KEYS.WORKSPACE, targetWorkspaceId);

  // 3. Mark impersonation state
  localStorage.setItem(KEYS.IS_IMPERSONATING, 'true');
  localStorage.setItem(KEYS.IMPERSONATION_REASON, reason);
  localStorage.setItem(KEYS.IMPERSONATION_TARGET, targetUserId);
}

/**
 * Exit impersonation — restores admin token
 * Returns false if no admin token found to restore
 */
export function exitImpersonation(): boolean {
  const adminToken = localStorage.getItem(KEYS.ADMIN_TOKEN);
  const adminWorkspace = localStorage.getItem(KEYS.ADMIN_WORKSPACE);

  if (adminToken) {
    localStorage.setItem(KEYS.TOKEN, adminToken);
  } else {
    localStorage.removeItem(KEYS.TOKEN);
  }

  if (adminWorkspace) {
    localStorage.setItem(KEYS.WORKSPACE, adminWorkspace);
  } else {
    localStorage.removeItem(KEYS.WORKSPACE);
  }

  // Clean up impersonation state
  localStorage.removeItem(KEYS.ADMIN_TOKEN);
  localStorage.removeItem(KEYS.ADMIN_WORKSPACE);
  localStorage.removeItem(KEYS.IS_IMPERSONATING);
  localStorage.removeItem(KEYS.IMPERSONATION_REASON);
  localStorage.removeItem(KEYS.IMPERSONATION_TARGET);

  return !!adminToken;
}

/**
 * Check if currently impersonating
 */
export function isImpersonating(): boolean {
  return localStorage.getItem(KEYS.IS_IMPERSONATING) === 'true';
}

/**
 * Get impersonation info for banner display
 */
export function getImpersonationInfo(): {
  isActive: boolean;
  targetUserId: string | null;
  reason: string | null;
} {
  return {
    isActive: isImpersonating(),
    targetUserId: localStorage.getItem(KEYS.IMPERSONATION_TARGET),
    reason: localStorage.getItem(KEYS.IMPERSONATION_REASON),
  };
}

/**
 * Full logout — clears everything including impersonation state
 */
export function fullLogout(): void {
  Object.values(KEYS).forEach(key => localStorage.removeItem(key));
}
